import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import { ActiveUserDto, activeUsersService } from '../api/services';

const RUOLO_COLOR: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  Admin: 'error',
  Coordinatore: 'primary',
  ResponsabileOrari: 'secondary',
  Dipendente: 'default',
  Linker: 'info',
  Maker: 'success',
  Referente: 'warning',
};

function minutiDa(isoStr: string): number {
  return Math.floor((Date.now() - new Date(isoStr).getTime()) / 60000);
}

function formatTime(isoStr: string): string {
  return new Date(isoStr).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
}

const UtentiAttivi: React.FC = () => {
  const [utenti, setUtenti] = useState<ActiveUserDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; msg: string }>({ open: false, msg: '' });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await activeUsersService.getAll();
      setUtenti(data);
    } catch (e: any) {
      setError(e?.response?.data || 'Errore nel caricamento degli utenti attivi');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 30_000); // refresh ogni 30s
    return () => clearInterval(interval);
  }, [load]);

  const handleKick = async (username: string) => {
    try {
      await activeUsersService.kick(username);
      setSnackbar({ open: true, msg: `Sessione di "${username}" terminata` });
      await load();
    } catch {
      setSnackbar({ open: true, msg: 'Errore nella terminazione della sessione' });
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          Utenti attivi ({utenti.length})
        </Typography>
        <Tooltip title="Aggiorna">
          <span>
            <IconButton onClick={load} disabled={loading} size="small">
              {loading ? <CircularProgress size={18} /> : <RefreshIcon />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>Nome</strong></TableCell>
              <TableCell><strong>Username</strong></TableCell>
              <TableCell><strong>Ruolo</strong></TableCell>
              <TableCell><strong>Negozio</strong></TableCell>
              <TableCell><strong>Login</strong></TableCell>
              <TableCell><strong>Ultima attività</strong></TableCell>
              <TableCell><strong>IP</strong></TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {utenti.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ color: 'text.secondary', py: 3 }}>
                  Nessun utente attivo
                </TableCell>
              </TableRow>
            )}
            {utenti.map((u) => {
              const minScorsi = minutiDa(u.lastActivity);
              return (
                <TableRow key={u.username} hover>
                  <TableCell>{u.nome}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{u.username}</TableCell>
                  <TableCell>
                    <Chip
                      label={u.ruolo}
                      size="small"
                      color={RUOLO_COLOR[u.ruolo] ?? 'default'}
                    />
                  </TableCell>
                  <TableCell>{u.negozio || '—'}</TableCell>
                  <TableCell>{formatTime(u.loginTime)}</TableCell>
                  <TableCell>
                    <Tooltip title={new Date(u.lastActivity).toLocaleString('it-IT')}>
                      <span style={{ color: minScorsi > 10 ? '#888' : 'inherit' }}>
                        {minScorsi < 1 ? '< 1 min fa' : `${minScorsi} min fa`}
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#888' }}>
                    {u.ipAddress || '—'}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Termina sessione">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleKick(u.username)}
                      >
                        <PersonOffIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ open: false, msg: '' })}
        message={snackbar.msg}
      />
    </Box>
  );
};

export default UtentiAttivi;
